<template lang="html">
  <v-row no-gutters>
    <FlvSplitPercent
      v-model="splitPercent"
      :total-value="Number(data.flv)"
    />

    <v-col
      cols="12"
      class="text-h6 pb-0"
      mt-4
    >
      Member spouse
    </v-col>

    <template v-if="reportType === 'contributor'">
      <v-col
        class="no-print mt-10 mb-3"
        cols="12"
      >
        <v-slider
          v-model="effectiveService"
          label="Effective service (years)"
          :min="data.tprs"
          :max="maxEffectiveService"
          thumb-label="always"
          hint="Estimated total period of effective service at retirement"
          @end="handleEffectiveServiceChange"
        >
          <template #thumb-label="{ value }">
            {{ value }}{{ value === maxEffectiveService ? '+' : '' }}
          </template>
        </v-slider>
      </v-col>

      <v-col class="only-print">
        <v-row class="">
          <v-col cols="6">
            Effective service
            <div class="subtitle-1 font-weight-bold">
              {{ effectiveService }}
            </div>
          </v-col>
        </v-row>
      </v-col> 
    </template>


    <flv-values :values="memberValuesOne" />

    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_member_one"
        class="no-print"
        width="100%"
        :height="250"
        :options="memberChartOneOptions"
        :series="memberSeriesOne"
      />
    </v-col>
      
    <v-col
      class="text-h6 pb-0"
      mt-4
    >
      Non-member spouse
    </v-col>

    <flv-values :values="nonMemberValues" />
        
    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_nonmember"
        class="no-print"
        width="100%"
        :height="250"
        :options="nonMemberChartOptions"
        :series="nonMemberSeries"
      />
    </v-col>
  </v-row>
</template>

<script>
import helpers from '@/mixins/helpers'
import VueApexCharts from 'vue-apexcharts'
import chartOptions from './chart-options'
import growthShared from './growth-shared'


export default {
  components: {
    apexcharts: VueApexCharts
  },
  mixins: [ helpers, chartOptions, growthShared ],
  props: {
    data: Object,
    tables: Object,
  },
  data () {
    return {
      splitPercent: 0,
      nonMemberSeries: [],
      memberSeriesOne: [],
      memberSeriesTwo: [],
      effectiveService: 0,
      maxEffectiveService: 40
    }
  },
  computed: {
    labels () {
      return {
        nonMember: {
          separationAmount: 'Transfer amount',
          fundedComponent: 'Funded component of transfer amount',
          unfundedComponent: 'Unfunded component of transfer amount',
        },
        member: {
          pensionMultiple: "Pension multiple - reduction amount",
          pensionMultiplePost: "Pension multiple - post reduction",
          productivity: 'Accumulated Productivity Contributions - reduction amount',
          productivityPost: 'Accumulated Productivity Contributions - post reduction',
          benefitReductionRate: 'Benefit rate of reduction',
        }
      }
    },
    reportType () {
      let { data: { valuation_type } } = this
      if (['dfrdb_contributor_under55', 'dfrdb_contributor_over55'].includes(valuation_type)) return 'contributor'
      if (['dfrdb_productivity_contributor_under55', 'dfrdb_productivity_contributor_over55'].includes(valuation_type)) return 'productivity'
       return null
    },
    apmsAtRetirement () {
      let table = this.tables.accrued_pension_multiples || {}
      return table[this.effectiveService]
    },
    productivityAmount () {
      let { valuation_type, pc, tpc, tipc } = this.data
      return valuation_type === 'dfrdb_productivity_contributor_over55' ? tpc + tipc : pc
    },
    memberValuesOne () {
      let { _$, labels, colors, reportType } = this 
      let primary = []
      let secondary = []

      switch (reportType) {
        case 'contributor':
          primary = [
            { label: labels.member.pensionMultiplePost, value: this.calcPensionMultiplePost(), color: colors[0] },
          ]
          secondary = [
            { label: labels.member.pensionMultiple, value: this.calcPensionMultipleReduction() },
          ]
          break
        case 'productivity':
          primary = [
            { label: labels.member.productivityPost, value: _$(this.calcProductivityPost(), 0), color: colors[0] },
          ]
          secondary = [
            { label: labels.member.productivity, value: _$(this.calcProductivityReduction(), 0) },
          ]
          break
        }

      return { primary, secondary }
    },
    memberChartOneOptions () {
      let { reportType } = this 
      let yFormatter = v => this._$(v, 0)
      let yMax = undefined

      switch (reportType) {
        case 'contributor': 
          yFormatter = v => v.toFixed(5)
          break
      }
      
      return this._chartOptions({
        ...this.sharedChartOptions,
        id: 'graph_member_one',
        stacked: true,
        yFormatter,
        yMax,
      })
    },
    
  },
  beforeMount () {
    this.graphNonMember()
  },
  mounted () {
    this.graphMemberOne()
  },
  methods: {
    graphMemberOne () {
      let { labels, reportType } = this

      let pensionMultipleSeries = { name: labels.member.pensionMultiplePost, type: 'line', data: [] }
      let productivitySeries = { name: labels.member.productivity, type: 'area', data: [] }

      this.xValues.forEach(percent => {
        let separationAmount = this.calcSeparationAmount(percent)
        let sfGross = this.calcSeparationFactorGross(separationAmount)

        pensionMultipleSeries.data.push(this.calcPensionMultiplePost(sfGross))
        productivitySeries.data.push(this.calcProductivityPost(sfGross))
      })
  
      switch (reportType) {
        case 'contributor': 
          this.memberSeriesOne = [ pensionMultipleSeries ]
          break
        case 'productivity': 
          this.memberSeriesOne = [ productivitySeries ]
          break
        default : 
        this.memberSeriesOne = []
      }
   
    },
    calcPensionMultipleReduction (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return (this.data.apms * sfGross).toFixed(5)
    },
    calcPensionMultiplePost (sfGross) {
      let pensionMultipleReduction = this.calcPensionMultipleReduction(sfGross)
      return (this.apmsAtRetirement - pensionMultipleReduction).toFixed(5)
    },
    calcProductivityReduction (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return (this.productivityAmount * sfGross)
    },
    calcProductivityPost (sfGross) {
      let productivityReduction = this.calcProductivityReduction(sfGross)
      return (this.productivityAmount - productivityReduction)
    },
    handleEffectiveServiceChange () {
      if (this.reportType === 'contributor') this.graphMemberOne()
    }
  }
}
</script>

<style lang="css">

</style>
